//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';

import { BVSamplingProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import { useBulkUploadProductsMutation } from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsQueries';
import { DefaultView } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/products-section/bulk-upload-modal/views/DefaultView';
import { ErrorView } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/products-section/bulk-upload-modal/views/ErrorView';
import { LoadingView } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/products-section/bulk-upload-modal/views/LoadingView';
import { SuccessView } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/products-section/bulk-upload-modal/views/SuccessView';
import { FileUploaderAssetFailure } from '@campaigns/components/shared/file-uploader/FileUploaderTypes';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { Modal } from '@components/molecules/modal/Modal';
import { isDefined } from '@utils/filterPredicates';

interface BulkUploadModalProps {
  closeModal: () => void;
  isModalOpen: boolean;
  onProductsImported: (products: BVSamplingProduct[]) => void;
}

export const BulkUploadModal = ({
  closeModal,
  isModalOpen,
  onProductsImported,
}: BulkUploadModalProps) => {
  const [fileName, setFileName] = useState<string>();
  const [importedProducts, setImportedProducts] = useState<BVSamplingProduct[]>(
    [],
  );

  const {
    error,
    isLoading,
    mutate: uploadProducts,
    reset,
  } = useBulkUploadProductsMutation();

  const resetForm = () => {
    setImportedProducts([]);
    setFileName(undefined);
    reset();
  };

  const handleCloseModal = () => {
    resetForm();
    closeModal();
  };

  const handleFilesSelected = (
    files: File[],
    failureReasons: FileUploaderAssetFailure,
  ) => {
    resetForm();

    if (!isDefined(files[0])) {
      return;
    }

    setFileName(files[0].name);
    uploadProducts(
      {
        file: files[0],
        failureReasons,
      },
      {
        onSuccess: (data) => {
          setImportedProducts(data);
        },
      },
    );
  };

  const handleImport = () => {
    onProductsImported(importedProducts);
    resetForm();
    closeModal();
  };

  const modalContent = (() => {
    if (isLoading) {
      return <LoadingView />;
    }

    if (error) {
      return (
        <ErrorView
          errors={error}
          fileName={fileName}
          onReplace={() => resetForm()}
        />
      );
    }

    if (importedProducts.length > 0) {
      return <SuccessView count={importedProducts.length} />;
    }

    return <DefaultView onFilesSelected={handleFilesSelected} />;
  })();

  const hasError = !!error;

  return (
    <Modal
      closeModal={handleCloseModal}
      contents={modalContent}
      isFooterEnabled
      isModalOpen={isModalOpen}
      primaryButtonInfo={{
        buttonContent: 'Import',
        onClick: handleImport,
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.BULK_UPLOAD_MODAL_IMPORT_BUTTON,
        isDisabled: isLoading || hasError || importedProducts.length === 0,
      }}
      secondaryButtonInfo={{
        buttonContent: 'Cancel',
        onClick: handleCloseModal,
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.BULK_UPLOAD_MODAL_CANCEL_BUTTON,
      }}
      title='Import Products'
      width='medium'
    />
  );
};
